import {
  getFunctionName,
  type FunctionReference,
  type FunctionReturnType,
} from 'convex/server';
import type { PaginatedQueryReference } from 'convex/react';

/** Order-independent JSON for plain query args (seed keys only). */
export function stableArgsJson(args: object): string {
  const sort = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(sort)
      : typeof value === 'object' && value !== null
        ? Object.fromEntries(
            Object.entries(value)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, entry]) => [key, sort(entry)])
          )
        : value;
  return JSON.stringify(sort(args));
}

export function serverSeedKey(
  kind: 'query' | 'paginated',
  query: FunctionReference<'query'>,
  args: object
): string {
  return `${kind}|${getFunctionName(query)}|${stableArgsJson(args)}`;
}

/** A seeded single-query result for `Query`. */
export type ServerQuerySeed<Query extends FunctionReference<'query'>> = {
  kind: 'query';
  functionName: string;
  data: FunctionReturnType<Query>;
};

/** A seeded first page of a paginated query. */
export type ServerPaginatedSeed<Query extends PaginatedQueryReference> = {
  kind: 'paginated';
  functionName: string;
  page: FunctionReturnType<Query>['page'];
  isDone: boolean;
};

export type ServerQuerySeeds = Record<
  string,
  | ServerQuerySeed<FunctionReference<'query'>>
  | ServerPaginatedSeed<PaginatedQueryReference>
>;

/** Seeds are keyed by function name; this guard ties an entry to `query`. */
export function isQuerySeedFor<Query extends FunctionReference<'query'>>(
  entry: ServerQuerySeeds[string] | undefined,
  query: Query
): entry is ServerQuerySeed<Query> {
  return (
    entry?.kind === 'query' && entry.functionName === getFunctionName(query)
  );
}

export function isPaginatedSeedFor<Query extends PaginatedQueryReference>(
  entry: ServerQuerySeeds[string] | undefined,
  query: Query
): entry is ServerPaginatedSeed<Query> {
  return (
    entry?.kind === 'paginated' && entry.functionName === getFunctionName(query)
  );
}
